import { isFileReference, missingCloudFile, missingCloudFileReference, remoteCloudFile, remoteCloudFileReference, type FileReference } from './cloudFileState';
import {acquireUploadTicket,uploadTicketIsSettled} from './uploadAdmission';
import {cloudReadFetch} from './cloudRead';
type TransferContext = { userId: string; token: string; signal: AbortSignal; queued?:boolean;onUploadWaiting?:()=>void;onUploadStart?:()=>void;localFiles?: () => Promise<Map<string, string>>; availability?: Map<string, boolean> };

const dataUrlFile = (value: string) => {
  const comma = value.indexOf(',');
  const header = value.slice(0, comma);
  const type = header.slice(5).split(';')[0] || 'application/octet-stream';
  const bytes = /;base64/i.test(header)
    ? Uint8Array.from(atob(value.slice(comma + 1)), (character) => character.charCodeAt(0))
    : new TextEncoder().encode(decodeURIComponent(value.slice(comma + 1)));
  return { bytes, type };
};

const sha256 = async (bytes: Uint8Array<ArrayBuffer>) => [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))]
  .map((byte) => byte.toString(16).padStart(2, '0')).join('');

const uploadFile = async (value: string, context: TransferContext, force = false): Promise<FileReference> => {
  const { bytes, type } = dataUrlFile(value);
  const digest = await sha256(bytes);
  const cacheKey = `bilaflow:sync-file:${context.userId}:${digest}`;
  let cached: unknown;
  if (!force) {
    try { cached = JSON.parse(localStorage.getItem(cacheKey) || 'null'); } catch { /* Optional cache. */ }
    if (isFileReference(cached) && cached.key.startsWith(`${context.userId}/sync/`)) {
      // A completed-upload cache is a hint, not proof that the physical R2
      // object still exists. A prefix may have been emptied outside the app.
      const check = await cloudReadFetch(`/api/files/${cached.key.split('/').map(encodeURIComponent).join('/')}`, {
        method: 'HEAD', cache: 'no-store', headers: { authorization: `Bearer ${context.token}` }, signal: context.signal,
      });
      if (check.ok) return cached;
      if (check.status !== 404 && check.status !== 405) throw new Error(`Cloud file verification failed (${check.status})`);
    }
  }
  let response:Response,result:{ key?: string; error?: string;state?:string };
  for(let attempt=0;;attempt++){
  const uploadTicket=context.queued?await acquireUploadTicket(digest,bytes.byteLength,{...context,onWaiting:context.onUploadWaiting}):null;
  context.onUploadStart?.();
  try{response = await fetch('/api/files', { method: 'POST', headers: {
    authorization: `Bearer ${context.token}`,'content-type':type,
    'x-bilaflow-upload':context.queued?'stream-v2':'stream-v1','x-bilaflow-file-size':String(bytes.byteLength),'x-bilaflow-file-sha256':digest,
    ...(uploadTicket?{'x-bilaflow-upload-ticket':uploadTicket}:{}),
  }, body: new Blob([bytes], {type}), signal: context.signal });
  result = await response.json().catch(()=>({error:'cloud_storage_unavailable'})) as { key?: string; error?: string;state?:string };
  }catch(error){
   context.signal.throwIfAborted();
   if(uploadTicket&&attempt<2&&await uploadTicketIsSettled(uploadTicket,digest,bytes.byteLength,{...context,onWaiting:context.onUploadWaiting}))continue;
   throw error;
  }
  if(context.queued&&attempt<2&&response.status===409&&result.error==='cloud_upload_ticket_expired')continue;
  if(uploadTicket&&attempt<2&&[502,503,504].includes(response.status)&&await uploadTicketIsSettled(uploadTicket,digest,bytes.byteLength,{...context,onWaiting:context.onUploadWaiting}))continue;
  break;
  }
  if (!response.ok || !result.key) throw new Error(result.error || 'R2 file upload failed');
  if(!result.key.startsWith(`${context.userId}/`))throw new Error('Cloud file permission denied');
  const reference: FileReference = { __bilaflowFile: 'r2-v1', key: result.key, type, sha256: digest, size: bytes.byteLength };
  if(result.state!=='staged')try { localStorage.setItem(cacheKey, JSON.stringify(reference)); } catch { /* Optional cache. */ }
  return reference;
};

const downloadFile = async (reference: FileReference, context: TransferContext, recovered: Map<string, FileReference>, missing: Map<string, FileReference>): Promise<string> => {
  if (!reference.key.startsWith(`${context.userId}/`)) throw new Error('Cloud file permission denied');
  // A verified manifest keeps a lazy reference, never all attachment bytes.
  if (context.availability?.get(reference.key) === true) return remoteCloudFile(reference);
  const response = context.availability?.get(reference.key) === false ? new Response(null, { status: 404 })
    : await cloudReadFetch(`/api/files/${reference.key.split('/').map(encodeURIComponent).join('/')}`, { cache: 'no-store', headers: { authorization: `Bearer ${context.token}` }, signal: context.signal });
  if (response.status === 404) {
    const files = await context.localFiles?.();
    const local = files?.get(reference.key) ?? (reference.sha256 && /^[0-9a-f]{64}$/.test(reference.sha256)
      ? files?.get(`${context.userId}/sync/${reference.sha256}`) : undefined);
    if (!local) { missing.set(reference.key, reference); return missingCloudFile(reference); }
    // Recover only bytes matched by their SHA-256 key or an account-scoped
    // digest/upload-cache association; never substitute a same-name file.
    const restored = await uploadFile(local, context, true);
    recovered.set(reference.key, restored);
    return local;
  }
  if (!response.ok) throw new Error(`R2 file download failed (${response.status})`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (reference.sha256 && await sha256(bytes) !== reference.sha256) throw new Error('Cloud file checksum mismatch');
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 8192) binary += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  return `data:${reference.type};base64,${btoa(binary)}`;
};

// Work on a copy: local documents and exports keep their original file data,
// while the cloud payload contains only private R2 references.
export const cloudFileTransfer = (context: TransferContext) => {
  const uploads = new Map<string, Promise<FileReference>>();
  const downloads = new Map<string, Promise<string>>();
  const recovered = new Map<string, FileReference>();
  const available = new Map<string, string>();
  const missing = new Map<string, FileReference>();
  const visit = async (value: unknown, direction: 'upload' | 'download'): Promise<unknown> => {
    context.signal.throwIfAborted();
    if (direction === 'upload') {
      const reference = missingCloudFileReference(value) || remoteCloudFileReference(value);
      if (reference) {
        if (!reference.key.startsWith(`${context.userId}/`)) throw new Error('Cloud file permission denied');
        return reference; // Preserve the original key; do not create empty files.
      }
    }
    if (direction === 'upload' && typeof value === 'string' && /^data:[^,]*,/i.test(value)) {
      let task = uploads.get(value);
      if (!task) { task = uploadFile(value, context); uploads.set(value, task); }
      return task;
    }
    if (direction === 'download' && isFileReference(value)) {
      let task = downloads.get(value.key);
      if (!task) { task = downloadFile(value, context, recovered, missing); downloads.set(value.key, task); }
      const result = await task;
      if (!missingCloudFileReference(result)) available.set(value.key, result);
      return result;
    }
    if (Array.isArray(value)) {
      const result: unknown[] = [];
      for (const item of value) result.push(await visit(item, direction));
      return result;
    }
    if (value && typeof value === 'object') {
      const result: Record<string, unknown> = {};
      for (const [key, item] of Object.entries(value)) result[key] = await visit(item, direction);
      return result;
    }
    return value;
  };
  return { upload: (value: unknown) => visit(value, 'upload'), download: (value: unknown) => visit(value, 'download'), recovered, available, missing };
};

export const referencesCloudKeys = (value: unknown, keys: Set<string>): boolean => {
  if (isFileReference(value)) return keys.has(value.key);
  return Boolean(value && typeof value === 'object' && Object.values(value).some((item) => referencesCloudKeys(item, keys)));
};

// Build lazily, only after a real 404. The database supplying these values is
// already scoped to the logged-in account; files in other accounts are never
// inspected. Local bytes remain intact throughout recovery.
export const localCloudFileSources = (values: unknown, userId: string, signal: AbortSignal) => {
  let pending: Promise<Map<string, string>> | undefined;
  return () => pending ??= (async () => {
    const dataUrls = new Set<string>();
    const collect = (value: unknown) => {
      if (typeof value === 'string' && /^data:[^,]*,/i.test(value)) dataUrls.add(value);
      else if (value && typeof value === 'object') Object.values(value).forEach(collect);
    };
    collect(values);
    const files = new Map<string, string>();
    for (const value of dataUrls) {
      signal.throwIfAborted();
      const digest = await sha256(dataUrlFile(value).bytes);
      files.set(`${userId}/sync/${digest}`, value);
      try {
        const cached = JSON.parse(localStorage.getItem(`bilaflow:sync-file:${userId}:${digest}`) || 'null') as unknown;
        if (isFileReference(cached) && cached.key.startsWith(`${userId}/sync/`)) files.set(cached.key, value);
      } catch { /* The canonical content-hash match still works without cache. */ }
    }
    return files;
  })();
};

export const hasCloudFileReferences = (value: unknown): boolean => {
  if (isFileReference(value)) return true;
  if (value && typeof value === 'object') return Object.values(value).some(hasCloudFileReferences);
  return false;
};

// Identify an already-acknowledged row after a partial upload without treating
// Local data URLs, lazy markers and equivalent SHA-256 references as conflicts.
// This comparison never writes files or changes the caller's original data.
export const cloudContentEquals = async (left: unknown, right: unknown): Promise<boolean> => {
  const normalize = async (value: unknown): Promise<unknown> => {
    const reference = missingCloudFileReference(value) || remoteCloudFileReference(value) || (isFileReference(value) ? value : null);
    if (reference) return { file: reference.sha256 || reference.key, type: reference.type };
    if (typeof value === 'string' && /^data:[^,]*,/i.test(value)) {
      const file = dataUrlFile(value); return { file: await sha256(file.bytes), type: file.type };
    }
    if (Array.isArray(value)) { const result: unknown[]=[]; for (const child of value) result.push(await normalize(child)); return result; }
    if (value && typeof value === 'object') {
      const result: Record<string,unknown>={};
      for (const [key,child] of Object.entries(value).sort(([a],[b])=>a.localeCompare(b))) {
        if (!['cloudRevision','cloudCatalogRevision','syncState'].includes(key) && child !== undefined) result[key]=await normalize(child);
      }
      return result;
    }
    return value;
  };
  return JSON.stringify(await normalize(left))===JSON.stringify(await normalize(right));
};

// Only cloud tombstones are stripped. Never mutate a local document or export.
export const stripDeletedCloudFiles = (value: unknown): unknown => {
  if (isFileReference(value) || missingCloudFileReference(value) || remoteCloudFileReference(value) || (typeof value === 'string' && /^data:[^,]*,/i.test(value))) return null;
  if (Array.isArray(value)) return value.map(stripDeletedCloudFiles);
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, stripDeletedCloudFiles(item)]));
  return value;
};

export const clearCloudFileCache = (userId: string) => {
  try {
    const prefix = `bilaflow:sync-file:${userId}:`;
    const keys: string[] = [];
    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
  } catch { /* Re-upload still works when the optional cache is unavailable. */ }
};
